import React, { useContext, useEffect } from 'react';
import {
  MDBContainer,
  MDBRow,
  MDBCol,
  MDBTypography,
  MDBBtn,
  MDBAlert,
  MDBCard,
  MDBCardBody,
  MDBBadge,
  MDBChip
} from 'mdb-react-ui-kit';
import { useTranslation } from 'react-i18next';
import { useFormData } from '../hooks/useFormData';
import { useStepNavigation } from '../hooks/useStepNavigation';
import { useValidation } from '../components/ValidationProvider';
import { useConfig } from '../config/ConfigProvider';
import { useEventData } from '../hooks/useEventData';
import { DevContext } from '../../../../App';
import { getTopicsLimits, lounges26Schema } from '../validation/lectures';
import { getLocalizedText } from '../../../../utils/localizedText';
import { useIndustry } from '../../../../utils/industryContextHook';

export default function TopicsStep({ onComplete, eventId }) {
  const { t, i18n } = useTranslation();
  const { formData, handleFieldChange, hasFieldError, getFieldError } = useFormData();
  const { goToNextStep, goToPreviousStep } = useStepNavigation();
  const { getStepValidation, getStepRequiredFields, getFieldValidation } = useValidation();
  const { getStepContent } = useConfig();
  const { isDevMode } = useContext(DevContext);
  const { currentIndustry } = useIndustry();

  // Get content from configuration
  const stepHeading = getStepContent('topics', 'heading');
  const stepDescription = getStepContent('topics', 'description');

  // Use the event data hook
  const {
    topics,
    topicGroups,
    fetchTopics,
    isLoading: isLoadingTopics
  } = useEventData(eventId, currentIndustry?.id);

  // Get validation info from schema
  const stepValidation = getStepValidation('topics');
  const requiredFields = getStepRequiredFields('topics');
  const topicsFieldValidation = getFieldValidation('topics');
  
  // Get min/max values from validation schema
  const topicsLimits = getTopicsLimits(lounges26Schema, formData);
  const minTopics = topicsLimits.min;
  const maxTopics = topicsLimits.max;

  // Get selected topics from form data
  const selectedTopics = formData.topics || [];

  // Fetch topics on mount if not already loaded
  useEffect(() => {
    if (topics.length === 0 && !isLoadingTopics) {
      fetchTopics();
    }
  }, [topics.length, isLoadingTopics, fetchTopics]);

  // Handle topic selection
  const handleTopicToggle = (topicId) => {
    const currentSelected = selectedTopics || [];
    const isSelected = currentSelected.includes(topicId);
    
    let newSelectedTopics;
    if (isSelected) {
      // Remove topic if already selected
      newSelectedTopics = currentSelected.filter(id => id !== topicId);
    } else {
      // Add topic if not selected and under limit
      if (currentSelected.length < maxTopics) {
        newSelectedTopics = [...currentSelected, topicId];
      } else {
        // If at limit, replace the first selected topic
        const remainingTopics = currentSelected.slice(1);
        newSelectedTopics = [...remainingTopics, topicId];
      }
    }
    
    handleFieldChange('topics', newSelectedTopics);
  };

  const handleContinue = () => {
    const isValid = selectedTopics.length >= minTopics && selectedTopics.length <= maxTopics && stepValidation.isValid;
    if (isValid) {
      onComplete?.();
      goToNextStep();
    }
  };

  const canContinue = selectedTopics.length >= minTopics && selectedTopics.length <= maxTopics && stepValidation.isValid;

  // Topic selection component - displays all topics as clickable chips
  const TopicSelection = ({ topics, topicGroups, selectedTopics }) => {
    if (topics?.length === 0 || topicGroups?.length === 0) {
      return (
        <MDBAlert color="info">
          {t('Loading topics...')}
        </MDBAlert>
      );
    }

    return (
      <MDBRow className="my-3 my-lg-4">
        {topicGroups.map((topicGroup) => (
          <MDBCol size="12" lg="6" key={topicGroup.id} className="mb-4">
            <div className="mb-3">
              <h6 className="fw-bold">
                {getLocalizedText(topicGroup.name)}
              </h6>
            </div>
            <div className="d-flex flex-wrap gap-2">
              {topicGroup.topics.items.map((topic) => {
                const isSelected = selectedTopics?.includes(topic.id);
                return (
                  <MDBChip
                    key={topic.id}
                    className="cursor-pointer m-0"
                    onClick={() => handleTopicToggle(topic.id)}
                    style={{ 
                      cursor: 'pointer', 
                      justifyContent: 'flex-start',
                      backgroundColor: selectedTopics?.includes(topic.id) ? "#3dd378" : "#ececec", 
                      color: selectedTopics?.includes(topic.id) ? "white" : "black"
                    }}
                  >
                    {getLocalizedText(topic.name)}
                  </MDBChip>
                );
              })}
            </div>
          </MDBCol>
        ))}
      </MDBRow>
    );
  };

  return (
    <div className="d-flex flex-column h-100">
      <MDBContainer className="flex-grow-1 pb-5">
      <MDBTypography tag="h3" className="mb-4">
        {stepHeading || t('Select topics')}
      </MDBTypography>
      
      <MDBRow className="mb-4">
        <MDBCol>
          <MDBTypography tag="p" className="mb-4 text-muted">
            {stepDescription || t(`Select ${minTopics} to ${maxTopics} topics that best describe your lecture. Click on the topics to select them.`)}
          </MDBTypography>
          
          <TopicSelection
            topics={topics}
            topicGroups={topicGroups}
            selectedTopics={selectedTopics}
          />
          
          {selectedTopics.length > 0 && (
            <div className="mb-3 text-center">
              <small className="text-muted">
                {selectedTopics.length}/{maxTopics} {t('topics selected')}
              </small>
            </div>
          )}
        </MDBCol>
      </MDBRow>

      {(hasFieldError('topics') || (selectedTopics.length < minTopics || selectedTopics.length > maxTopics)) && (
        <MDBRow className="mb-3">
          <MDBCol>
            <MDBAlert color="danger">
              {getFieldError('topics') || t(`Please select between ${minTopics} and ${maxTopics} topics`)}
            </MDBAlert>
          </MDBCol>
        </MDBRow>
      )}

      {/* Validation Debug Info */}
      {isDevMode && (
        <MDBRow className="mb-4">
          <MDBCol>
            <MDBCard className="border-info">
              <MDBCardBody>
                <MDBTypography tag="h6" className="mb-3">
                  🔍 Validation Debug Info (Dev Mode) - Topics Step
                </MDBTypography>
              
              <div className="mb-2">
                <strong>Step Required Fields:</strong> {requiredFields.join(', ') || 'none'}
              </div>
              
              <div className="mb-2">
                <strong>Selected Topics:</strong>{' '}
                {selectedTopics.length > 0 ? (
                  <>
                    {selectedTopics.length} topics selected: {selectedTopics.map(topicId => {
                      const topic = topics.find(t => t.id === topicId);
                      return getLocalizedText(topic?.name) || topicId;
                    }).join(', ')}
                  </>
                ) : (
                  'None selected'
                )}
              </div>
              
              <div className="mb-2">
                <strong>Local Validation:</strong>{' '}
                <MDBBadge color={selectedTopics.length >= minTopics && selectedTopics.length <= maxTopics ? 'success' : 'warning'}>
                  {selectedTopics.length >= minTopics && selectedTopics.length <= maxTopics ? `Valid (${minTopics}-${maxTopics} topics)` : `Invalid (needs ${minTopics}-${maxTopics} topics)`}
                </MDBBadge>
              </div>
              
              <div className="mb-2">
                <strong>Schema Validation:</strong>{' '}
                <MDBBadge color={stepValidation.isValid ? 'success' : 'danger'}>
                  {stepValidation.isValid ? 'Valid' : 'Invalid'}
                </MDBBadge>
              </div>
              
              {stepValidation.errors.length > 0 && (
                <div className="mb-2">
                  <strong>Schema Errors:</strong>
                  <ul className="mb-0">
                    {stepValidation.errors.map((error, index) => (
                      <li key={index}>
                        <code>{error.field}</code>: {error.message}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              {topicsFieldValidation && (
                <div className="mb-2">
                  <strong>Field-Specific Error:</strong>{' '}
                  <code>{topicsFieldValidation.field}</code>: {topicsFieldValidation.message}
                </div>
              )}
              
              <div className="mb-2">
                <strong>Continue Button:</strong>{' '}
                <MDBBadge color={canContinue ? 'success' : 'warning'}>
                  {canContinue ? 'Enabled' : 'Disabled'}
                </MDBBadge>
              </div>
            </MDBCardBody>
          </MDBCard>
        </MDBCol>
      </MDBRow>
      )}

      </MDBContainer>
      
      {/* Fixed button container at bottom */}
      <div className="bg-white" style={{ 
        position: 'sticky', 
        bottom: 0,
        padding: '1rem',
        marginTop: 'auto',
        zIndex: 10
      }}>
        <MDBContainer>
          <MDBRow>
            <MDBCol className="d-flex justify-content-center gap-5">
              <MDBBtn 
                color="secondary" 
                onClick={goToPreviousStep}
                outline
              >
                {t('Back')}
              </MDBBtn>
              <MDBBtn 
                onClick={handleContinue}
                disabled={!canContinue}
              >
                {t('Continue')}
              </MDBBtn>
            </MDBCol>
          </MDBRow>
        </MDBContainer>
      </div>
    </div>
  );
}